import './home.scss';

import React, { useContext, useEffect, useState } from 'react';

import { DateSelectArg, EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Button } from 'primereact/button';
import { useAppSelector } from 'app/config/store';
import FullCalendar from '@fullcalendar/react';
import { Dialog } from 'primereact/dialog';
import DateTimeTextInput from 'app/component/date-time-text-input';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { deleteCalendarEvent, getAllEvents, saveCalendarEvent, updateCalendarEvent } from 'app/api/calendarEventApi';
import { ToastContext } from 'app/context/ToastContext';
import { formatDateFromInstant } from 'app/shared/util/date-utils';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { ProgressSpinner } from 'primereact/progressspinner';

export const CalendarEventComponent = () => {
  const account = useAppSelector(state => state.authentication.account);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [view, setView] = useState('');
  const { showSuccess, showError } = useContext(ToastContext);
  const [changedEvent, setChangedEvent] = useState(null);

  const onEventClick = (e: EventClickArg) => {
    const { id } = e.event;

    setView('display');
    setShowDialog(true);

    const selectedEvent = events.find(ev => {
      return ev.id.toString() === id;
    });

    setChangedEvent(selectedEvent);
  };

  useEffect(() => {
    getAllEvents()
      .then(response => {
        const d = response.data.map(m => ({
          ...m,
        }));

        setEvents(d);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = () => {
    setLoading(true);

    let call;

    if (changedEvent.id) {
      call = updateCalendarEvent(changedEvent.id, changedEvent);
    } else {
      call = saveCalendarEvent(changedEvent);
    }

    call
      .then(response => {
        setEvents(response.data);

        setShowDialog(false);
        setChangedEvent({});
        showSuccess('Úspech', 'Udalosť sa úspešne uložila.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onEditClick = () => {
    setView('edit');
  };

  const onDeleteClick = () => {
    setLoading(true);
    deleteCalendarEvent(changedEvent.id)
      .then(response => {
        setEvents(response.data);

        setShowDialog(false);
        setChangedEvent({});
        showSuccess('Úspech', 'Udalosť úspešne vymazaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDateSelect = (e: DateSelectArg) => {
    if (hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT])) {
      setView('new');
      setChangedEvent({});
      setShowDialog(true);
    }
  };

  const footer = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT]) && (
    <>
      {view === 'display' ? (
        <div className="justify-content-between">
          <Button
            style={{ marginRight: '30px', backgroundColor: 'gray' }}
            className="p-button-secondary"
            type="button"
            label="Vymazať udalosť"
            icon="pi pi-bin"
            onClick={onDeleteClick}
          />
          <Button
            style={{ backgroundColor: 'gray' }}
            className="p-button-secondary"
            type="button"
            label="Upraviť"
            icon="pi pi-pencil"
            onClick={onEditClick}
          />
        </div>
      ) : null}
      {view === 'new' || view === 'edit' ? (
        <Button
          type="button"
          label="Uložiť"
          style={{ backgroundColor: '#e31818' }}
          className="p-button-danger"
          icon="pi pi-check"
          disabled={!changedEvent?.start || !changedEvent?.end}
          onClick={handleSave}
        />
      ) : null}
    </>
  );

  return (
    <>
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loading && (
        <FullCalendar
          events={events}
          eventClick={onEventClick}
          select={onDateSelect}
          initialDate={new Date()}
          initialView="dayGridMonth"
          height={720}
          locale="sk"
          firstDay={1}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{
            left: 'prev,next',
            right: 'title',
            // right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          // datesSet={handleDatesSet}
          editable
          selectable
          selectMirror
          dayMaxEvents
          displayEventTime={false}
        />
      )}
      <Dialog
        visible={showDialog}
        style={{ width: '36rem' }}
        modal
        headerClassName="text-900 font-semibold text-xl"
        header={view === 'display' ? changedEvent.title : view === 'new' ? 'Nová udalosť' : 'Upraviť udalosť'}
        breakpoints={{ '960px': '75vw', '640px': '90vw' }}
        footer={footer}
        closable
        onHide={() => {
          setShowDialog(false);
          setChangedEvent({});
        }}
      >
        <>
          {view === 'display' ? (
            <React.Fragment>
              <span className="text-900 font-semibold block mb-2">Opis</span>
              <span className="block mb-3" style={{ whiteSpace: 'pre-wrap' }}>{changedEvent.description}</span>

              <div className="grid">
                <div className="col-6">
                  <div className="text-900 font-semibold mb-2">Začiatok</div>
                  <p className="flex align-items-center m-0">
                    <i className="pi pi-fw pi-clock text-700 mr-2"></i>
                    <span>{formatDateFromInstant(changedEvent.start?.toString())}</span>
                  </p>
                </div>
                <div className="col-6">
                  <div className="text-900 font-semibold mb-2">Koniec</div>
                  <p className="flex align-items-center m-0">
                    <i className="pi pi-fw pi-clock text-700 mr-2"></i>
                    <span>{formatDateFromInstant(changedEvent.end?.toString())}</span>
                  </p>
                </div>
                <div className="col-12">
                  <div className="text-900 font-semibold mb-2">Miesto</div>
                  <p className="flex align-items-center m-0">
                    <i className="pi pi-fw pi-map-marker text-700 mr-2"></i>
                    <span>{changedEvent.location}</span>
                  </p>
                </div>
              </div>
            </React.Fragment>
          ) : (
            <div className="grid p-fluid formgrid">
              <div className="col-12 md:col-6 field">
                <label htmlFor="title" className="text-900 font-semibold">
                  Názov udalosti
                </label>
                <span className="p-input-icon-left">
                  <i className="pi pi-pencil"></i>
                  <InputText
                    id="title"
                    value={changedEvent?.title}
                    onChange={e =>
                      setChangedEvent(prevState => ({
                        ...prevState,
                        title: e.target.value,
                      }))
                    }
                    type="text"
                    placeholder="Názov udalosti"
                  />
                </span>
              </div>
              <div className="col-12 md:col-6 field">
                <label htmlFor="location" className="text-900 font-semibold">
                  Miesto
                </label>
                <span className="p-input-icon-left">
                  <i className="pi pi-map-marker"></i>
                  <InputText
                    id="location"
                    value={changedEvent?.location}
                    onChange={e =>
                      setChangedEvent(prevState => ({
                        ...prevState,
                        location: e.target.value,
                      }))
                    }
                    type="text"
                    placeholder="Miesto"
                  />
                </span>
              </div>
              <div className="col-12 field">
                <label htmlFor="description" className="text-900 font-semibold">
                  Opis udalosti
                </label>
                <InputTextarea
                  id="description"
                  rows={5}
                  autoResize
                  value={changedEvent?.description}
                  onChange={e =>
                    setChangedEvent(prevState => ({
                      ...prevState,
                      description: e.target.value,
                    }))
                  }
                  style={{ resize: 'none' }}
                ></InputTextarea>
              </div>

              <div className="col-12 md:col-6 field">
                <label htmlFor="start" className="text-900 font-semibold">
                  Začiatok
                </label>
                <DateTimeTextInput
                  id="start"
                  value={changedEvent?.start ? new Date(changedEvent.start) : null}
                  onChange={date =>
                    setChangedEvent(prevState => ({
                      ...prevState,
                      start: date,
                    }))
                  }
                  required
                />
              </div>
              <div className="col-12 md:col-6 field">
                <label htmlFor="end" className="text-900 font-semibold">
                  Koniec
                </label>
                <DateTimeTextInput
                  id="end"
                  value={changedEvent?.end ? new Date(changedEvent.end) : null}
                  onChange={date =>
                    setChangedEvent(prevState => ({
                      ...prevState,
                      end: date,
                    }))
                  }
                  required
                />
              </div>
            </div>
          )}
        </>
      </Dialog>
    </>
  );
};

export default CalendarEventComponent;
